/**
 * ============================================================================
 * STORAGE QUOTA COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Display storage usage and quota
 */

import React, { useState, useEffect } from 'react';
import { useStorage } from '../../hooks/useStorage';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import ProgressBar from '../common/ProgressBar';
import Spinner from '../common/Spinner';
import Button from '../common/Button';

/**
 * Storage Quota Component
 * @param {Object} props
 * @param {number} props.used - Used storage in bytes
 * @param {number} props.limit - Storage limit in bytes
 * @param {Function} props.onRefresh - Refresh callback
 * @returns {JSX.Element}
 */
const StorageQuota = ({ used, limit, onRefresh }) => {
  const { loading } = useStorage();
  const { showError } = useToast();
  const { language } = useI18n();

  const formatBytes = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const usedBytes = used || 0;
  const limitBytes = limit || 10 * 1024 * 1024 * 1024; // 10GB default
  const percentage = limitBytes > 0 ? (usedBytes / limitBytes) * 100 : 0;
  const remaining = limitBytes - usedBytes;

  const getQuotaColor = () => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'success';
  };

  const getQuotaVariant = () => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    return 'info';
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
            <span className="text-2xl">💾</span>
            {language === 'fr' ? 'Quota de stockage' : 'Storage Quota'}
          </h3>
          {onRefresh && (
            <Button variant="ghost" size="sm" onClick={onRefresh} loading={loading}>
              🔄
            </Button>
          )}
        </div>

        {/* Usage Display */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {language === 'fr' ? 'Utilisé' : 'Used'}
            </span>
            <span className="text-lg font-black text-gray-900 dark:text-white">
              {formatBytes(usedBytes)}
            </span>
          </div>

          <ProgressBar
            value={percentage}
            variant={getQuotaVariant()}
            animated
            showLabel
            label={`${percentage.toFixed(1)}%`}
          />

          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              {language === 'fr' ? 'Restant' : 'Remaining'}: {formatBytes(remaining)}
            </span>
            <span>
              {language === 'fr' ? 'Limite' : 'Limit'}: {formatBytes(limitBytes)}
            </span>
          </div>
        </div>

        {/* Warning */}
        {percentage >= 75 && (
          <Card
            variant="outlined"
            className={`${
              percentage >= 90
                ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800'
            }`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚠️</span>
              <div>
                <p
                  className={`font-bold mb-1 ${
                    percentage >= 90
                      ? 'text-red-800 dark:text-red-200'
                      : 'text-orange-800 dark:text-orange-200'
                  }`}
                >
                  {percentage >= 90
                    ? language === 'fr'
                      ? 'Quota presque atteint!'
                      : 'Quota Almost Reached!'
                    : language === 'fr'
                    ? 'Quota élevé'
                    : 'High Quota Usage'}
                </p>
                <p
                  className={`text-sm ${
                    percentage >= 90
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-orange-700 dark:text-orange-300'
                  }`}
                >
                  {language === 'fr'
                    ? 'Supprimez des fichiers inutilisés pour libérer de l\'espace'
                    : 'Delete unused files to free up space'}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Breakdown */}
        <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Images' : 'Images'}
            </p>
            <p className="text-sm font-black text-blue-600 dark:text-blue-400">
              {formatBytes(usedBytes * 0.4)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Documents' : 'Documents'}
            </p>
            <p className="text-sm font-black text-green-600 dark:text-green-400">
              {formatBytes(usedBytes * 0.5)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {language === 'fr' ? 'Autres' : 'Other'}
            </p>
            <p className="text-sm font-black text-purple-600 dark:text-purple-400">
              {formatBytes(usedBytes * 0.1)}
            </p>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default StorageQuota;
